import {
  clearAuthConfig,
  clearTokens,
  getAuthConfigFromStorage,
  getTokens,
  saveAuthConfig,
} from '../utils/authStorage';
import store from '../redux/store';
import { refreshAuthToken } from '../redux/reducers/authInfoReducer';
import { AuthConfig } from '../models/auth/AuthConfig';
import { AuthToken } from '../models/auth/AuthToken';
import { generateCodeChallenge, generateCodeVerifier, saveVerifier } from './pkce';
import { clearViewStack } from '../redux/reducers/pageViewsReducer';
import { getAuthConfig } from './resource';

/**
 * OAuth2 client ID
 */
export const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID ?? 'ui_client';

/**
 * URI для редиректа после успешной авторизации
 */
const redirectUri = window.location.origin + '/callback';

/**
 * Относительный путь до точки входа логина
 */
const loginUrl = 'oauth2/authorize';

/**
 * Возвращает конфигурацию приложения.
 * Бросает ошибку, если она ещё не установлена.
 *
 * @returns AppConfig
 */
export const getAppConfig = (): AuthConfig => {
  const config = getAuthConfigFromStorage();
  if (!config) throw new Error('AppConfig not initialized');
  return config;
};

/**
 * Флаг, указывающий, выполняется ли в текущий момент операция обновления токена.
 * Это предотвращает множественные параллельные запросы на refresh.
 */
let isRefreshing = false;

/**
 * Список функций-колбэков, ожидающих получения валидного access_token после refresh.
 */
let subscribers: Array<(token: string) => void> = [];

/**
 * Добавляет колбэк в очередь ожидания обновлённого access_token.
 *
 * @param cb Колбэк, вызываемый при получении нового access_token
 */
const subscribe = (cb: (token: string) => void) => subscribers.push(cb);

/**
 * Уведомляет всех подписчиков о получении нового access_token и очищает список подписчиков.
 *
 * @param token Новый access_token
 */
const notifySubscribers = (token: string) => {
  subscribers.forEach((cb) => cb(token));
  subscribers = [];
};

export const buildLoginUrl = async (baseUrl: string) => {
  const verifier = generateCodeVerifier();
  saveVerifier(verifier);

  const challenge = await generateCodeChallenge(verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    scope: 'openid offline_access',
    redirect_uri: redirectUri,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  // если используете state — добавь тут
  const state = new URLSearchParams(window.location.search).get('state');
  if (state) params.set('state', state);

  return `${baseUrl}/${loginUrl}?${params.toString()}`;
};

/**
 * Выполняет очистку токенов и перенаправляет пользователя на страницу логина.
 * Используется в случае отсутствия токенов или ошибки при обновлении.
 */
export const redirectToLogin = async () => {
  clearTokens();
  const config = getAppConfig();
  window.location.href = await buildLoginUrl(config.baseUrl);
};

/**
 * Завершает сессию: очищает локальные токены и редиректит на /logout сервера
 * с параметрами PKCE.
 */
export const redirectToLogout = async () => {
  const config = getAppConfig();
  clearTokens();
  store.dispatch(clearViewStack());

  const verifier = generateCodeVerifier();
  saveVerifier(verifier);
  const challenge = await generateCodeChallenge(verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    scope: 'openid offline_access',
    redirect_uri: redirectUri,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${config.baseUrl}/logout?${params.toString()}`;
};

/**
 * Проверяет, валиден ли текущий access token.
 * Если токен отсутствует или его срок действия истёк — возвращает `false`.
 *
 * @param tokens Объект токенов для проверки.
 * @returns `true`, если access_token существует и ещё не истёк; иначе `false`.
 */
const checkAccessTokenValid = (tokens: AuthToken | null | undefined): boolean => {
  if (!tokens?.valid_until || !tokens.access_token) {
    return false;
  }

  const validUntilDate = new Date(tokens.valid_until);
  if (isNaN(validUntilDate.getTime())) {
    return false;
  }

  return Date.now() < validUntilDate.getTime();
};

/**
 * Вспомогательная функция для получения актуальных токенов
 */
const requireTokens = (): AuthToken => {
  const tokens = getTokens();
  if (!tokens?.access_token || !tokens?.refresh_token) {
    throw { response: { status: 401, message: 'No tokens available' } };
  }
  return tokens;
};

/**
 * Получает валидный access_token, выполняя refresh при необходимости.
 *
 * Поведение:
 * - Если токен валиден — возвращает его.
 * - Если истёк, но есть refresh_token — обновляет и возвращает новый.
 * - Если обновление уже идёт — подписывается и ждёт его завершения.
 * - Если обновление не удалось — редиректит на login.
 *
 * @returns Promise<string | null> access_token либо null, если авторизация невозможна
 */
export const getValidAccessToken = async (): Promise<string> => {
  const tokens = requireTokens();

  if (checkAccessTokenValid(tokens)) {
    return tokens.access_token;
  }

  if (isRefreshing) {
    return new Promise((resolve) => subscribe(resolve));
  }

  isRefreshing = true;

  try {
    const newAccessToken = await refreshToken();
    notifySubscribers(newAccessToken);
    return newAccessToken;
  } catch (err) {
    throw { response: { status: 401, message: 'refresh token expired' } };
  } finally {
    isRefreshing = false;
  }
};

/**
 * Пробует обновить access_token с использованием refresh_token.
 * Если успешно — возвращает новый access_token.
 * Если неудачно — кидает исключение.
 */
export const refreshToken = async (): Promise<string> => {
  const tokens = requireTokens();

  const action = await store.dispatch(refreshAuthToken(tokens.refresh_token));

  if (refreshAuthToken.fulfilled.match(action)) {
    const newTokens = requireTokens();
    if (newTokens?.access_token) {
      return newTokens.access_token;
    }
  }

  throw new Error('Unable to refresh access token');
};

/**
 * Обновляет конфиг сервера авторизации при ошибках >401
 */
export const refreshAuthConfig = async () => {
  clearAuthConfig();

  const { data } = await getAuthConfig();

  saveAuthConfig(data);

  return data;
};